import { hideVisually, rgba, size } from "polished";
import * as React from "react";
import styled, { css } from "styled-components";
import i18n from "../i18n";
import search from "../img/icons/search.svg";
import select from "../img/icons/select.svg";
import theme from "../theme";
import { isRTL } from "../utils/locale";

export const FieldWrap = styled.div`
  margin-bottom: ${theme.space[4]};
`;

interface ILabelProps {
  labelVisibility?: boolean;
}

const FieldLabel = styled.label<ILabelProps>`
  display: block;
  margin-bottom: ${theme.space[1]};
  cursor: pointer;

  ${({ labelVisibility }) => labelVisibility === false && hideVisually};
`;

const Hint = styled.span`
  margin-bottom: ${theme.space[1]};
  display: block;
  color: ${theme.colors.offBlack};
  font-weight: 100;
  font-size: 14px;
`;

interface InputProps {
  hasErrors?: boolean;
  required?: boolean;
}

const InputWrap = styled.div`
  position: relative;
  border: ${({ theme }) => theme.borders[1]};
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.greyLighter};
`;

const FieldStyles = css`
  display: block;
  width: 100%;
  padding: ${(props) => props.theme.space[2]};
  border: 1px solid ${(props) => rgba(props.theme.colors.greyBlue, 0.1)};
  border-radius: ${(props) => props.theme.radii[0]};
  background-color: ${(props) => props.theme.colors.greyLight};
  color: ${(props) => props.theme.colors.offBlack};
  font-size: 16px;
`;

const Input = styled.input<InputProps>`
  ${FieldStyles}
  ${(props) =>
    props.hasErrors &&
    css`
      border-color: ${theme.colors.error};

      &:focus {
        border-color: ${theme.colors.error};
      }
    `}
`;

const SelectWrap = styled.div`
  position: relative;
  z-index: ${theme.zIndex.select.wrap};
  border-radius: ${(props) => props.theme.radii[0]};
  background-color: ${(props) => props.theme.colors.greyLighter};

  &::after {
    content: "";
    position: absolute;
    background: url(${select}) no-repeat;
    top: 50%;
    transform: translateY(-50%);
    /* right: 10px; */
    ${size(8, 15)};
    background-size: contain;
    ${
      isRTL(i18n.language)
        ? css`
            left: 10px;
          `
        : css`
            right: 10px;
          `
    }
  }
`;

// Unable to extend SelectWrap for some reason
const InlineSelectWrap = styled.div`
  position: relative;
  display: inline-block;
  z-index: ${theme.zIndex.select.wrap};
  border: grey;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-size: contain;
    /* Triangle */
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: blue;
    ${
      isRTL(i18n.language)
        ? css`
            left: 10px;
          `
        : css`
            right: 10px;
          `
    }
  }
`;

const Select = styled.select`
  ${FieldStyles}
  position: relative;
  z-index: ${theme.zIndex.select.select};
  background-color: transparent;
  appearance: none;
  cursor: pointer;

  &::-ms-expand {
    display: none; /* [1] */
  }
`;

const InlineSelect = styled(Select)`
  display: inline-block;
  width: auto;
  ${
    isRTL(i18n.language)
      ? css`
          padding-left: 40px;
        `
      : css`
          padding-right: 40px;
        `
  }
`;

const SearchInput = styled(Input)`
  ${
    isRTL(i18n.language)
      ? css`
          padding-right: 45px;
        `
      : css`
          padding-left: 45px;
        `
  }

  /* stylelint-disable property-no-vendor-prefix */
  -webkit-appearance: textfield;
  appearance: textfield;
`;

const SearchIcon = styled.span`
  ${size("32px")}
  position: absolute;
  top: 50%;
  left: ${(props) => props.theme.space[2]};
  border-radius: 50%;
  transform: translateY(-50%);
  background: ${(props) => props.theme.colors.yellow} url(${search}) no-repeat
    center;
  background-size:
    16px 16px,
    auto;
  ${
    isRTL(i18n.language)
      ? css`
          right: ${(props) => props.theme.space[2]};
        `
      : css`
          left: ${(props) => props.theme.space[2]};
        `
  }
`;

export const FieldCols = styled.div`
  position: relative;
  z-index: 1;
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
  }
`;

export const FieldCol = styled.div`
  padding-right: ${({ theme }) => theme.space[2]};
  padding-left: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1;
  }
`;

interface ILabelHintProps {
  hint?: React.ReactNode;
  id: string;
  label: React.ReactNode;
  labelVisibility?: boolean;
  required?: boolean;
}

interface IFieldProps extends ILabelHintProps {
  name?: string;
  value?: string | number;
}

interface IInputProps extends IFieldProps {
  maxLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  readonly?: boolean;
  hasErrors?: boolean;
  required?: boolean;
}

const LabelHint: React.FC<ILabelHintProps> = ({
  id,
  label,
  hint,
  required = false,
  labelVisibility = true,
}) => (
  <FieldLabel htmlFor={id} labelVisibility={labelVisibility}>
    <span>{`${label}${required ? "*" : ""}`}</span>
    {hint && <Hint>{hint}</Hint>}
  </FieldLabel>
);

const InlineLabel: React.FC<ILabelHintProps> = ({
  id,
  label,
  labelVisibility = true,
}) => (
  <FieldLabel htmlFor={id} labelVisibility={labelVisibility}>
    <span>{label}</span>
  </FieldLabel>
);

export const InputField: React.FC<IInputProps> = ({
  hint,
  id,
  label,
  maxLength,
  name,
  onBlur,
  onChange,
  type,
  value,
  required = true,
  hasErrors,
  disabled = false,
  readonly = false,
  labelVisibility = true,
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      hint={hint}
      labelVisibility={labelVisibility}
      required={required}
    />
    <InputWrap>
      <Input
        required={required}
        type={type}
        id={id}
        name={name}
        onChange={onChange}
        value={value}
        onBlur={onBlur}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readonly}
        hasErrors={hasErrors}
      />
    </InputWrap>
  </>
);

interface ISearchProps extends IFieldProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export const SearchField: React.FC<ISearchProps> = ({
  hint,
  id,
  label,
  labelVisibility,
  name,
  onChange,
  value,
  ...rest
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      hint={hint}
      labelVisibility={labelVisibility}
    />
    <InputWrap>
      <SearchIcon />
      <SearchInput
        type="search"
        id={id}
        name={name}
        onChange={onChange}
        value={value}
        {...rest}
      />
    </InputWrap>
  </>
);

const Checkbox = styled.input`
  ${hideVisually()};

  &:hover + span::before {
    border-color: black;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="75" height="75"><path d="M28.051 68.625c-1.199 0-2.324-.449-3.149-1.273L5.254 47.102c-1.875-1.727-1.949-4.648-.149-6.523 1.727-1.875 4.649-1.949 6.524-.148l14.398 10.273c.825.75 2.102.602 2.774-.301L62.926 8.25c1.5-2.023 4.426-2.477 6.449-.898 2.023 1.5 2.477 4.425.898 6.449L31.727 66.75c-.825 1.051-2.024 1.727-3.301 1.801-.153.074-.227.074-.375.074z" fill="%23E8E8E8"/></svg>');
  }

  &:focus + span {
    outline: -webkit-focus-ring-color auto 5px;
  }

  &:checked + span::before {
    background-color: black;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="75" height="75"><path d="M28.051 68.625c-1.199 0-2.324-.449-3.149-1.273L5.254 47.102c-1.875-1.727-1.949-4.648-.149-6.523 1.727-1.875 4.649-1.949 6.524-.148l14.398 10.273c.825.75 2.102.602 2.774-.301L62.926 8.25c1.5-2.023 4.426-2.477 6.449-.898 2.023 1.5 2.477 4.425.898 6.449L31.727 66.75c-.825 1.051-2.024 1.727-3.301 1.801-.153.074-.227.074-.375.074z" fill="white" /></svg>');
  }
`;

interface ICheckboxText {
  hasLogo?: boolean;
}

const CheckboxText = styled.span<ICheckboxText>`
  display: inline-block;
  position: relative;
  padding: 6px 0 6px 42px;
  line-height: 1.6;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    width: 30px;
    height: 30px;
    transform: translateY(-50%);
    border: 1px solid black;
    background-repeat: no-repeat;
    background-size: contain;
    vertical-align: middle;
  }

  ${({ hasLogo }) =>
    hasLogo &&
    css`
      display: flex;
      align-items: center;
    `}
`;

interface ICheckboxProps {
  checked?: boolean;
  id: string;
  label: React.ReactNode;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  logo?: any;
  hideLabel?: boolean;
}

const LogoImg = styled.img`
  height: 29px;
  width: auto;
  max-width: 100%;
`;

export const CheckboxField: React.FC<ICheckboxProps> = ({
  id,
  onChange,
  label,
  checked = false,
  logo,
  hideLabel = false,
}) => {
  let labelStyles = {};
  if (hideLabel) {
    labelStyles = hideVisually;
  }

  return (
    <div>
      <label htmlFor={id}>
        <Checkbox
          type="checkbox"
          id={id}
          role="checkbox"
          checked={checked}
          aria-checked={checked}
          onChange={onChange}
        />
        <CheckboxText hasLogo={logo}>
          {hideLabel === false && <span style={labelStyles}>{label}</span>}
          {logo && <LogoImg src={logo} />}
        </CheckboxText>
      </label>
    </div>
  );
};

const StyledFieldFeedback = styled.p`
  color: ${theme.colors.error};
  margin-top: 0;
`;

interface ISelectProps extends IFieldProps {
  children: React.ReactNode;
  onBlur?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  labelVisibility?: boolean;
}

export const SelectField: React.FC<ISelectProps> = ({
  children,
  hint,
  id,
  label,
  onBlur,
  onChange,
  value,
  labelVisibility = true,
  required = true,
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      hint={hint}
      labelVisibility={labelVisibility}
      required={required}
    />
    <InputWrap>
      <SelectWrap>
        <Select id={id} onBlur={onBlur} onChange={onChange} value={value}>
          {children}
        </Select>
      </SelectWrap>
    </InputWrap>
  </>
);

export const InlineSelectField: React.FC<ISelectProps> = ({
  children,
  id,
  label,
  onChange,
  value,
}) => (
  <>
    <InlineLabel id={id} label={label} />
    <InlineSelectWrap>
      <InlineSelect id={id} onChange={onChange} value={value}>
        {children}
      </InlineSelect>
    </InlineSelectWrap>
  </>
);

interface IFieldFeedback {
  children: React.ReactNode;
}

export const FieldFeedback: React.FC<IFieldFeedback> = ({ children }) => (
  <StyledFieldFeedback>{children}</StyledFieldFeedback>
);
